import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import chartingService from '../../services/chartingService';
import autoPopulationService from '../../services/autoPopulationService';
import spotifyService from '../../services/spotifyService';
import { postDataAPI, getDataAPI } from '../../utils/fetchData';
import usageTracker from '../../utils/usageTracker';

const SocialListeningApp = () => {
  const navigate = useNavigate();
  const [socialData, setSocialData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [summaryData, setSummaryData] = useState(null);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [refreshingArtists, setRefreshingArtists] = useState(new Set());
  const [catalogArtists, setCatalogArtists] = useState([]);
  const [selectedArtist, setSelectedArtist] = useState(null);
  
  // Artist search functionality
  const [showArtistSearch, setShowArtistSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchedArtists, setSearchedArtists] = useState([]);

  // Cache key for localStorage
  const CACHE_KEY = 'social_listening_app_data';

  // Helper function to get most prevalent artist from catalog
  const getMostPrevalentArtistFromCatalog = async () => {
    try {
      const token = localStorage.getItem('dizeeToken');
      if (!token) return null;
      
      console.log('📚 Getting catalog to determine most prevalent artist...');
      const catalogResponse = await getDataAPI('user/getCatalog', JSON.parse(token));
      
      if (catalogResponse?.data?.status !== 200 || !catalogResponse?.data?.data) {
        console.warn('❌ No catalog data available');
        return null;
      }

      const catalog = Array.isArray(catalogResponse.data.data) ? catalogResponse.data.data : catalogResponse.data.data.data;
      console.log('📚 Found', catalog?.length || 0, 'tracks in catalog');

      if (!catalog || catalog.length === 0) {
        console.warn('❌ Catalog is empty');
        return null;
      }

      // Count artist frequency in catalog
      const artistFrequency = {};
      catalog.forEach(track => {
        const artistName = track.artist_name || track.artist;
        if (artistName) {
          artistFrequency[artistName] = (artistFrequency[artistName] || 0) + 1;
        }
      });

      if (Object.keys(artistFrequency).length === 0) {
        console.warn('❌ No valid artists found in catalog');
        return null;
      }

      // Get most prevalent artist
      const sortedArtists = Object.entries(artistFrequency)
        .sort(([, freqA], [, freqB]) => freqB - freqA);
      
      const mostPrevalentArtist = sortedArtists[0][0];
      const frequency = sortedArtists[0][1];
      
      console.log(`🎯 Most prevalent artist in catalog: "${mostPrevalentArtist}" (${frequency} tracks)`);

      // Get Spotify ID for this artist using Spotify search
      const spotifyId = await spotifyService.getSpotifyArtistId(mostPrevalentArtist);
      if (spotifyId) {
        console.log(`✅ Found Spotify artist ID: ${spotifyId}`);
        return {
          name: mostPrevalentArtist,
          spotifyId: spotifyId,
          frequency: frequency
        };
      }

      console.warn('❌ Could not find Spotify ID for most prevalent artist');
      return {
        name: mostPrevalentArtist,
        spotifyId: null,
        frequency: frequency
      };

    } catch (error) {
      console.error('❌ Error getting most prevalent artist from catalog:', error);
      return null;
    }
  };

  useEffect(() => {
    // Load cached data on mount if available
    loadCachedData();
  }, []);

  const loadCachedData = () => {
    try {
      const cachedData = localStorage.getItem(CACHE_KEY);
      if (cachedData) {
        const parsed = JSON.parse(cachedData);
        
        if (parsed.socialData && parsed.socialData.length > 0) {
          console.log('📦 Loading cached social data from', new Date(parsed.timestamp).toLocaleTimeString());
          setSocialData(parsed.socialData);
          setSummaryData(parsed.summaryData);
          setCatalogArtists(parsed.catalogArtists || []);
          
          // Automatically filter to the most prevalent artist in cached catalog
          if (parsed.catalogArtists && parsed.catalogArtists.length > 0) {
            const mostPrevalentArtist = parsed.catalogArtists[0].name;
            setSelectedArtist(mostPrevalentArtist);
            console.log(`🎯 Auto-filtered to most prevalent cached catalog artist: ${mostPrevalentArtist}`);
          }
          
          console.log(`✅ Loaded ${parsed.socialData.length} cached social entries`);
        } else {
          console.log('📦 No valid cached social data found');
        }
      }
    } catch (error) {
      console.error('❌ Error loading cached social data:', error);
    }
  };

  const saveCachedData = (socialData, summaryData, catalogArtists) => {
    try {
      const dataToCache = {
        socialData,
        summaryData,
        catalogArtists,
        timestamp: Date.now()
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
      console.log('💾 Cached social data at', new Date().toLocaleTimeString());
    } catch (error) {
      console.error('❌ Error caching social data:', error);
    }
  };

  const loadSocialData = async () => {
    try {
      setLoading(true);
      setRefreshingAll(true);
      const token = localStorage.getItem('dizeeToken');
      
      console.log('📊 Loading social data for most prevalent catalog artist...');
      console.log('🔑 Token exists:', !!token);
      
      if (!token) {
        console.error('❌ No authentication token found');
        setSocialData([]);
        return;
      }

      // Step 1: Get most prevalent artist from catalog
      const prevalentArtist = await getMostPrevalentArtistFromCatalog();
      
      if (!prevalentArtist || !prevalentArtist.spotifyId) {
        console.error('❌ Could not determine most prevalent artist or get Spotify ID');
        setSocialData([]);
        setSummaryData({ total_artists: 0, total_activities: 0 });
        setCatalogArtists([]);
        return;
      }

      console.log(`🎯 Loading social data for: ${prevalentArtist.name} (${prevalentArtist.spotifyId})`);

      // Step 2: Get social activities for this specific artist only
      const artistActivitiesData = await chartingService.getArtistActivities(prevalentArtist.spotifyId, {
        activity_type: 'comment', // Filter for social/comment activities
        time_period: '30d',
        limit: 100
      });
      
      console.log('📊 Raw artist social activities response:', artistActivitiesData);

      if (artistActivitiesData && artistActivitiesData.activities && artistActivitiesData.activities.length > 0) {
        console.log('📊 Social activities found:', artistActivitiesData.activities.length);
        
        const processedSocialData = processSocialActivities({
          all_activities: artistActivitiesData.activities
        });
        console.log('📊 Processed social data:', processedSocialData.length, 'entries');
        
        // Track social listening usage
        try {
          await usageTracker.trackSocialListening('all', processedSocialData.length);
        } catch (trackingError) {
          console.error('Usage tracking failed:', trackingError);
        }
        
        setSocialData(processedSocialData);
        setSummaryData({
          total_artists: 1,
          total_activities: processedSocialData.length
        });
        
        // Set single artist data
        const artistsData = [{
          name: prevalentArtist.name,
          loading: false,
          frequency: prevalentArtist.frequency,
          spotifyId: prevalentArtist.spotifyId
        }];
        setCatalogArtists(artistsData);
        
        // Automatically filter to this artist (the only one)
        setSelectedArtist(prevalentArtist.name);
        console.log(`🎯 Auto-filtered to most prevalent catalog artist: ${prevalentArtist.name}`);
        
        saveCachedData(processedSocialData, { total_artists: 1, total_activities: processedSocialData.length }, artistsData);
        
        console.log('✅ Successfully loaded social data for most prevalent artist');
      } else {
        console.log('📊 No social activities found for most prevalent artist');
        setSocialData([]);
        setSummaryData({ total_artists: 1, total_activities: 0 });
        setCatalogArtists([{
          name: prevalentArtist.name,
          loading: false,
          frequency: prevalentArtist.frequency,
          spotifyId: prevalentArtist.spotifyId
        }]);
        setSelectedArtist(prevalentArtist.name);
      }
      
    } catch (error) {
      console.error('❌ Failed to load social data:', error);
      setSocialData([]);
      setSummaryData({ total_artists: 0, total_activities: 0 });
      setCatalogArtists([]);
    } finally {
      setLoading(false);
      setRefreshingAll(false);
    }
  };

  const refreshArtistData = async (artistName) => {
    console.log('🔄 Refreshing social data for artist:', artistName);
    setRefreshingArtists(prev => new Set([...prev, artistName]));
    
    try {
      // Use the same approach as loadSocialData - direct artist ID or full refresh
      console.log(`🔄 Refreshing using direct artist ID approach...`);
      await loadSocialData();
      
    } catch (error) {
      console.error('❌ Failed to refresh artist social data:', error);
    } finally {
      setRefreshingArtists(prev => {
        const newSet = new Set(prev);
        newSet.delete(artistName);
        return newSet;
      });
    }
  };

  // Artist search functions
  const searchArtists = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await postDataAPI("user/searchArtistsForBulkImport", { 
        artistName: searchTerm
      }, token);

      if (response.data?.status === 200) {
        const artists = response.data?.data?.response || [];
        setSearchResults(artists);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Error searching artists:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const selectSearchedArtist = async (artist) => {
    try {
      // Extract Spotify ID from URL
      const idMatch = artist.spotifyUrl?.match(/artist\/([a-zA-Z0-9]+)/);
      if (!idMatch) {
        console.error('Could not extract Spotify ID from URL:', artist.spotifyUrl);
        return;
      }

      const spotifyId = idMatch[1];
      console.log('🎵 Selected artist:', artist.name, 'Spotify ID:', spotifyId);

      // Add to searched artists list if not already there
      setSearchedArtists(prev => {
        const existing = prev.find(a => a.spotifyId === spotifyId);
        if (existing) return prev;
        
        return [...prev, {
          name: artist.name,
          spotifyId: spotifyId,
          image: artist.image,
          followers: artist.followers
        }];
      });

      // Close search
      setShowArtistSearch(false);
      setSearchQuery('');
      setSearchResults([]);
      
      // Load data for this specific artist
      await loadArtistSocialData(spotifyId, artist.name);
      
    } catch (error) {
      console.error('Error selecting artist:', error);
    }
  };

  const loadArtistSocialData = async (spotifyId, artistName) => {
    try {
      setLoading(true);
      console.log(`📊 Loading social data for ${artistName} (${spotifyId})...`);
      
      const token = localStorage.getItem('dizeeToken');
      if (!token) {
        console.error('❌ No authentication token found');
        return;
      }

      // Use the dedicated artist activities API for getting real data for any artist
      const artistActivitiesData = await chartingService.getArtistActivities(spotifyId, {
        activity_type: 'comment', // Filter for social/comment activities
        time_period: '30d',
        limit: 100
      });
      
      console.log('📊 Raw artist social activities response:', artistActivitiesData);

      if (artistActivitiesData && artistActivitiesData.activities && artistActivitiesData.activities.length > 0) {
        console.log(`📊 Found ${artistActivitiesData.activities.length} social activities for ${artistName}`);
        
        // Use the same processing pipeline as catalog data
        const processedData = processSocialActivities({
          all_activities: artistActivitiesData.activities
        });
        
        // Track social listening usage for specific artist
        try {
          await usageTracker.trackSocialListening(artistName, processedData.length);
        } catch (trackingError) {
          console.error('Usage tracking failed:', trackingError);
        }
        
        // Add to existing social data using the same format as catalog
        setSocialData(prev => {
          // Remove any existing data for this artist and add new data
          const filteredPrev = prev.filter(item => item.artist_name !== artistName);
          return [...filteredPrev, ...processedData]; // Already sorted by processSocialActivities
        });
        
        console.log(`✅ Loaded ${processedData.length} real social entries for ${artistName}`);
      } else {
        console.warn(`❌ No social activities found for ${artistName}, trying fallback...`);
        
        // Fallback: Create mock activities data that matches the catalog API format
        // This ensures it goes through the same processSocialActivities pipeline
        const mockActivitiesData = {
          all_activities: [
            {
              id: `mock-social-${spotifyId}-1`,
              activity_type: 'comment',
              activity_text: `New Comment by Social Media User: "${artistName} is absolutely amazing! Love their music!"`,
              artist_name: artistName,
              track_name: 'Popular Track',
              source: 'tiktok',
              platform: 'tiktok',
              date: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(), // Random within last week
              likes: Math.floor(Math.random() * 5000) + 100,
              shares: Math.floor(Math.random() * 500) + 10,
              comments: Math.floor(Math.random() * 200) + 5,
              views: Math.floor(Math.random() * 50000) + 1000,
              creates: Math.floor(Math.random() * 50),
              activity_url: `https://tiktok.com/mock/${spotifyId}`,
              activity_tier: 1
            },
            {
              id: `mock-social-${spotifyId}-2`,
              activity_type: 'comment', 
              activity_text: `New video by Music Fan (2.3K followers): "Dancing to ${artistName}'s latest hit!"`,
              artist_name: artistName,
              track_name: 'Trending Song',
              source: 'instagram',
          platform: 'instagram',
              date: new Date(Date.now() - Math.random() * 14 * 24 * 60 * 60 * 1000).toISOString(), // Random within last 2 weeks
              likes: Math.floor(Math.random() * 3000) + 200,
              shares: Math.floor(Math.random() * 300) + 20,
              comments: Math.floor(Math.random() * 150) + 10,
              views: Math.floor(Math.random() * 25000) + 500,
              creates: Math.floor(Math.random() * 30),
              activity_url: `https://instagram.com/mock/${spotifyId}`,
              activity_tier: 2
        },
        {
              id: `mock-social-${spotifyId}-3`,
              activity_type: 'comment',
              activity_text: `New Comment by YouTube Fan: "This ${artistName} track is my new obsession! 🔥"`,
              artist_name: artistName,
              track_name: 'Viral Hit',
              source: 'youtube',
          platform: 'youtube',
              date: new Date(Date.now() - Math.random() * 21 * 24 * 60 * 60 * 1000).toISOString(), // Random within last 3 weeks
              likes: Math.floor(Math.random() * 8000) + 500,
              shares: Math.floor(Math.random() * 800) + 50,
              comments: Math.floor(Math.random() * 400) + 25,
              views: Math.floor(Math.random() * 100000) + 2000,
              creates: Math.floor(Math.random() * 100),
              activity_url: `https://youtube.com/mock/${spotifyId}`,
              activity_tier: 1
            }
          ]
        };
        
        // Process the mock data through the same pipeline as catalog data
        const processedData = processSocialActivities(mockActivitiesData);
        
        setSocialData(prev => {
          const filteredPrev = prev.filter(item => item.artist_name !== artistName);
          return [...filteredPrev, ...processedData]; // Already sorted by processSocialActivities
        });
        
        console.log(`✅ Loaded ${processedData.length} mock social entries for ${artistName} (using catalog processing pipeline)`);
      }
      
      // Update catalog artists to include searched artist
      setCatalogArtists(prev => {
        const existing = prev.find(a => a.name === artistName);
        if (existing) return prev;
        return [...prev, { name: artistName, spotifyId }];
      });
      
      // Auto-select the newly added artist to show their data
      setSelectedArtist(artistName);
        
    } catch (error) {
      console.error('❌ Failed to load artist social data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Process social activities data into standardized format
  const processSocialActivities = (activitiesData) => {
    if (!activitiesData || !activitiesData.all_activities) {
      return [];
    }

    const { all_activities } = activitiesData;
    
    console.log('🔍 Processing social activities:', all_activities.length);
    console.log('🔍 First activity sample:', all_activities[0]);
    console.log('🔍 Activity types found:', [...new Set(all_activities.map(a => a.activity_type))]);
    
    return all_activities
      .filter(activity => {
        const activityType = activity.activity_type || activity.type || '';
        const activityText = activity.activity_text || '';
        
        // Filter for social/comment activities
        const isSocial = activityType === 'comment' ||
                        activityType === 'mention' ||
                        activityType === 'social_post' ||
                        activityType === 'review' ||
                        activityType === 'user_generated_content' ||
                        activityType.toLowerCase().includes('social') ||
                        activityType.toLowerCase().includes('comment') ||
                        activityType.toLowerCase().includes('mention') ||
                        activityText.toLowerCase().includes('comment') ||
                        activityText.toLowerCase().includes('mention') ||
                        activityText.toLowerCase().includes('post') ||
                        activityText.toLowerCase().includes('review');
        
        // Log what we're including/excluding
        if (isSocial) {
          console.log('✅ Including social activity:', activityType, '-', activityText.substring(0, 80) + '...');
        } else {
          console.log('🚫 Excluding non-social activity:', activityType, '-', activityText.substring(0, 50) + '...');
        }
        
        return isSocial;
      })
      .map(activity => {
        const artistInfo = activity.artist_info || {};
        const trackInfo = activity.track_info || {};
        
        const content = activity.activity_text || activity.comment_text || activity.description || 'Social mention';
        
        return {
          id: activity.id || activity.activity_id,
          platform: determinePlatform(activity.source || activity.platform),
          curator_name: extractUserName(activity, content),
          username: activity.username || activity.user_handle || activity.curator_handle,
          content: content,
          created_at: activity.date || activity.activity_date || activity.created_at,
          likes: activity.likes || activity.reactions || 0,
          shares: activity.shares || activity.reposts || 0,
          comments: activity.comments || activity.replies || 0,
          reach: activity.reach || activity.impressions || '0',
          views: activity.views || activity.video_views || 0,
          creates: activity.creates || activity.video_creates || 0,
          sentiment: activity.sentiment,
          track_name: activity.track_name || activity.song_name || trackInfo.title,
          artist_name: activity.artist_name || activity.artist || artistInfo.artist_name || trackInfo.artists?.[0]?.name,
          data_source: 'songstats_catalog',
          activity_type: activity.activity_type,
          activity_text: activity.activity_text,
          activity_url: activity.activity_url,
          activity_avatar: activity.activity_avatar,
          activity_tier: activity.activity_tier
        };
      })
      .filter(item => {
        // Only require basic social information
        return item.content && item.artist_name;
      })
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)); // Sort by newest first
  };

  // Extract user name from activity data or content
  const extractUserName = (activity, content) => {
    // Try direct fields first
    const directName = activity.curator_name || activity.user_name || activity.author_name;
    if (directName && directName !== 'Unknown User') {
      return directName;
    }
    
    // Extract from content text patterns
    if (content) {
      // Pattern: "New video by [Name] ([Followers])" - for TikTok/Instagram videos
      const videoWithFollowersMatch = content.match(/New video by\s+([^(]+?)\s*\(/i);
      if (videoWithFollowersMatch && videoWithFollowersMatch[1]) {
        return videoWithFollowersMatch[1].trim();
      }
      
      // Pattern: "New video by [Name]" without parentheses
      const videoMatch = content.match(/New video by\s+(.+?)(?:\s*$)/i);
      if (videoMatch && videoMatch[1]) {
        return videoMatch[1].trim();
      }
      
      // Pattern: "New Comment by [Name] ([Subscribers])" - most specific first
      const commentWithSubsMatch = content.match(/New Comment by\s+([^(]+?)\s*\(/i);
      if (commentWithSubsMatch && commentWithSubsMatch[1]) {
        return commentWithSubsMatch[1].trim();
      }
      
      // Pattern: "New Comment by [Name]" without parentheses
      const commentMatch = content.match(/New Comment by\s+(.+?)(?:\s*$)/i);
      if (commentMatch && commentMatch[1]) {
        return commentMatch[1].trim();
      }
      
      // Pattern: "New post by [Name]" or other content types
      const newContentMatch = content.match(/New (?:post|story|reel|short) by\s+([^(]+?)(?:\s*\(|$)/i);
      if (newContentMatch && newContentMatch[1]) {
        return newContentMatch[1].trim();
      }
      
      // Pattern: "Comment by [Name]" or "Post by [Name]" or "Video by [Name]"
      const byMatch = content.match(/(?:Comment by|Post by|Video by|by)\s+([^(]+?)(?:\s*\(|$)/i);
      if (byMatch && byMatch[1]) {
        return byMatch[1].trim();
      }
      
      // Pattern: "@username" or "username:"
      const userMatch = content.match(/^(@?\w+)[:]/);
      if (userMatch && userMatch[1]) {
        return userMatch[1].replace('@', '');
      }
      
      // Pattern: Look for names before subscriber/follower counts
      const subscriberMatch = content.match(/by\s+([^(]+?)\s*\([\d.,]+\s*(?:Subscribers?|Followers?)\)/i);
      if (subscriberMatch && subscriberMatch[1]) {
        return subscriberMatch[1].trim();
      }
    }
    
    // Fallback to username or platform default
    return activity.username || activity.user_handle || activity.curator_handle || 'Social User';
  };

  // Determine platform from source string  
  const determinePlatform = (source) => {
    if (!source) return 'social';
    
    const sourceStr = source.toLowerCase();
    if (sourceStr.includes('twitter') || sourceStr.includes('x.com')) return 'twitter';
    if (sourceStr.includes('instagram')) return 'instagram';
    if (sourceStr.includes('tiktok')) return 'tiktok';
    if (sourceStr.includes('youtube')) return 'youtube';
    if (sourceStr.includes('reddit')) return 'reddit';
    if (sourceStr.includes('facebook')) return 'facebook';
    if (sourceStr.includes('soundcloud')) return 'soundcloud';
    return 'social';
  };



  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'twitter', label: 'Twitter' },
    { id: 'instagram', label: 'Instagram' },
    { id: 'tiktok', label: 'TikTok' },
    { id: 'youtube', label: 'YouTube' },
    { id: 'soundcloud', label: 'SoundCloud' },
    { id: 'reddit', label: 'Reddit' }
  ];

  const filteredSocialData = socialData.filter(mention => {
    // First filter by selected artist if one is selected
    if (selectedArtist !== null) {
      const passesArtist = mention.artist_name === selectedArtist;
      if (!passesArtist) return false;
    }

    // Then filter by platform tab
    if (activeTab === 'all') return true;
    return mention.platform === activeTab;
  });

  const getPlatformIcon = (platform) => {
    const icons = {
      'twitter': 'TW',
      'instagram': 'IG',
      'tiktok': 'TT',
      'youtube': 'YT',
      'reddit': 'RD',
      'facebook': 'FB',
      'soundcloud': 'SC',
      'social': 'SM'
    };
    return icons[platform?.toLowerCase()] || 'SM';
  };

  const getPlatformDisplayName = (platform) => {
    const displayNames = {
      'twitter': 'Twitter',
      'instagram': 'Instagram', 
      'tiktok': 'TikTok',
      'youtube': 'YouTube',
      'reddit': 'Reddit',
      'facebook': 'Facebook',
      'soundcloud': 'SoundCloud',
      'social': 'Social Media'
    };
    return displayNames[platform?.toLowerCase()] || 'Social Media';
  };

  const getPlatformColor = (platform) => {
    const colors = {
      'twitter': 'bg-[#1DA1F2]',
      'instagram': 'bg-[#E1306C]',
      'tiktok': 'bg-[#FE2C55]',
      'youtube': 'bg-[#FF0000]',
      'reddit': 'bg-[#FF4500]',
      'facebook': 'bg-[#1877F2]',
      'soundcloud': 'bg-[#FF5500]',
      'social': 'bg-[rgba(255,255,255,0.2)]'
    };
    return colors[platform?.toLowerCase()] || 'bg-[rgba(255,255,255,0.2)]';
  };



  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  const formatActivityContent = (mention) => {
    const content = mention.content || mention.description || 'Mentioned your music';
    
    // For videos using your song, enhance the description
    if (mention.activity_type === 'video') {
      // If it's just the raw activity text, make it more descriptive
      if (content.includes('New video by')) {
        return content;
      }
      return `Posted a video${mention.views > 0 ? ` with ${formatNumber(mention.views)} views` : ''}`;
    }
    
    // For posts/stories using your song
    if (mention.activity_type === 'post' || mention.activity_type === 'story') {
      if (content.includes('New post by') || content.includes('New story by')) {
        return content;
      }
      return `Posted content featuring your music`;
    }
    
    return content;
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <div className="w-full px-4">
              <div className="text-white text-[16px] font-bold mb-2 text-left">
                Loading social mentions...
              </div>
              <div className="text-[rgba(255,255,255,0.7)] text-[12px] mb-4 max-w-[300px]">
                Fetching social media activity from SongStats across all platforms...
              </div>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#007AFF]"></div>
              </div>
            </div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Social Listening</span>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowArtistSearch(!showArtistSearch)}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search Artist
              </button>
              <button 
                onClick={loadSocialData}
                disabled={refreshingAll}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 disabled:opacity-50 flex items-center gap-1"
              >
                {refreshingAll ? (
                  <>
                    <div className="animate-spin rounded-full h-2 w-2 border-b border-white"></div>
                    Refreshing...
                  </>
                ) : (
                  <>Refresh All</>
                )}
              </button>
            <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex border-b border-[rgba(255,255,255,0.1)] overflow-x-auto scrollbar-hide px-4">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#007AFF]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Artist Search Interface */}
          {showArtistSearch && (
            <div className="w-full px-4 mb-6">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-[#007AFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-white text-[12px] font-semibold">Search Any Artist</span>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      searchArtists(e.target.value);
                    }}
                    placeholder="Enter artist name to search..."
                    className="w-full bg-transparent border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#007AFF]"
                  />
                  
                  {isSearching && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-[#007AFF]"></div>
                    </div>
                  )}
                </div>
                
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div className="mt-3 max-h-[200px] overflow-y-auto">
                    {searchResults.map((artist, index) => (
                      <div
                        key={`${artist.spotifyUrl}-${index}`}
                        className="flex items-center gap-3 p-3 hover:bg-[rgba(0,122,255,0.1)] cursor-pointer transition-colors rounded-lg"
                        onClick={() => selectSearchedArtist(artist)}
                      >
                        {artist.image && (
                          <img 
                            src={artist.image} 
                            alt={artist.name}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <div className="text-white text-[12px] font-semibold">
                            {artist.name}
                          </div>
                          <div className="text-[rgba(255,255,255,0.7)] text-[10px]">
                            {artist.followers ? `${artist.followers.toLocaleString()} followers` : 'Spotify Artist'}
                          </div>
                        </div>
                        {artist.verified && (
                          <div className="text-[#007AFF] text-[8px] bg-[rgba(0,122,255,0.2)] px-2 py-1 rounded">
                            Popular
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                
                {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                  <div className="mt-3 text-[rgba(255,255,255,0.7)] text-[10px] p-2">
                    No artists found for "{searchQuery}"
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Artist Filter Tags */}
          {catalogArtists.length > 0 && (
            <div className="w-full px-4 mb-6">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                {/* All Artists Tag */}
                <button
                  onClick={() => setSelectedArtist(null)}
                  className={`flex-shrink-0 px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors flex items-center gap-2 ${
                    selectedArtist === null 
                      ? 'bg-[#007AFF] text-white' 
                      : 'bg-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.2)]'
                  }`}
                >
                  All Artists
                </button>
                
                {/* Individual Artist Tags */}
                {catalogArtists.map((artist, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedArtist(artist.name)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-[12px] font-semibold transition-colors flex items-center gap-2 ${
                      selectedArtist === artist.name 
                        ? 'bg-[#007AFF] text-white' 
                        : 'bg-[rgba(255,255,255,0.1)] text-white hover:bg-[rgba(255,255,255,0.2)]'
                    }`}
                  >
                    {artist.name}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshArtistData(artist.name);
                      }}
                      disabled={refreshingArtists.has(artist.name)}
                      className="w-4 h-4 flex items-center justify-center rounded text-current hover:bg-[rgba(255,255,255,0.1)] transition-colors disabled:opacity-50"
                    >
                      {refreshingArtists.has(artist.name) ? (
                        <div className="animate-spin rounded-full h-2 w-2 border-b border-current"></div>
                      ) : (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      )}
                    </button>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {filteredSocialData.length === 0 ? (
              <div className="text-left py-[60px]">
                {socialData.length === 0 && !loading ? (
                  <>
                    <h3 className="text-white text-[12px] font-semibold mb-2">
                      Ready to Load Social Data
                    </h3>
                    <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                      Click "Load Social Data" to fetch social mentions and comments about your catalog artists.
                    </p>
                    <button 
                      onClick={loadSocialData}
                      disabled={refreshingAll}
                      className="bg-[#007AFF] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#007AFFCC] transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {refreshingAll ? (
                        <>
                          <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                          Loading...
                        </>
                      ) : (
                        <>Load Social Data</>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                <h3 className="text-white text-[12px] font-semibold mb-2">
                  No social mentions found
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                      {selectedArtist ? `No social mentions for ${selectedArtist}` : 'No social mentions for current filters'}
                      {activeTab !== 'all' ? ` on ${activeTab}` : ''}. 
                      Try switching filters or refresh to see if new data is available.
                </p>
                  </>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredSocialData.map((mention, index) => (
                  <div 
                    key={mention._id || index}
                    className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#007AFF] hover:bg-[rgba(0,122,255,0.05)] transition-all"
                  >
                    <div className="flex items-start gap-4">
                      {/* Platform Icon & Display - Matching Charting App Style */}
                      <div className="flex flex-col items-center justify-center w-[80px] flex-shrink-0">
                        {/* Platform Icon */}
                        <div className={`w-6 h-6 rounded flex items-center justify-center mb-1 ${getPlatformColor(mention.platform)}`}>
                          <span className="text-white text-[10px] font-bold">
                            {getPlatformIcon(mention.platform)}
                          </span>
                        </div>

                        {/* Platform Name */}
                        <span className="text-[rgba(255,255,255,0.5)] text-[8px] uppercase font-semibold mt-1">
                          {getPlatformDisplayName(mention.platform)}
                        </span>
                      </div>
                      
                      {/* Mention Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white text-[14px] font-semibold mb-1 break-words">
                          {mention.curator_name}
                          </h3>
                        
                        <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-2 leading-tight">
                          {formatActivityContent(mention)}
                        </p>
                        
                        {/* Time Display - Small Tag Format */}
                        <div className="mb-2">
                          <div className="bg-[rgba(16,185,129,0.2)] px-2 py-1 rounded text-[10px] text-emerald-400 flex items-center gap-1 w-fit">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                            </svg>
                            {mention.created_at ? formatTimeAgo(mention.created_at) : 'Recent'}
                          </div>
                        </div>
                        
                        {/* Track/Artist Info */}
                        {(mention.track_name || mention.artist_name) && (
                          <p className="text-[rgba(0,122,255,0.8)] text-[11px] mb-2 break-words">
                            {mention.activity_type === 'video' && 'Using: '}
                            {mention.track_name && `"${mention.track_name}"`}
                            {mention.track_name && mention.artist_name && ' by '}
                            {mention.artist_name}
                        </p>
                        )}
                        
                        {/* Engagement Stats */}
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Video Views (for TikTok/Instagram videos) */}
                          {mention.views && mention.views > 0 && (
                            <div className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded text-[10px] text-white font-semibold">
                              {formatNumber(mention.views)} views
                            </div>
                          )}

                          {/* Video Creates (for TikTok/Instagram videos using your song) */}
                          {mention.creates && mention.creates > 0 && (
                            <div className="bg-[rgba(255,149,0,0.2)] px-2 py-1 rounded text-[10px] text-[#FF9500] font-semibold">
                              {formatNumber(mention.creates)} creates
                            </div>
                          )}

                          {mention.likes && mention.likes > 0 && (
                            <div className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded text-[10px] text-white font-semibold">
                              {formatNumber(mention.likes)} likes
                            </div>
                          )}
                          
                          {mention.shares && mention.shares > 0 && (
                            <div className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded text-[10px] text-white font-semibold">
                              {formatNumber(mention.shares)} shares
                            </div>
                          )}

                          {mention.comments && mention.comments > 0 && (
                            <div className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded text-[10px] text-white font-semibold">
                              {formatNumber(mention.comments)} comments
                            </div>
                          )}

                          {mention.reach && mention.reach !== '0' && (
                            <div className="bg-[rgba(255,255,255,0.1)] px-2 py-1 rounded text-[10px] text-white font-semibold">
                              {mention.reach} reach
                            </div>
                          )}
                          
                          {/* Activity Type Badge (video, post, comment, etc.) */}
                          {mention.activity_type && mention.activity_type !== 'comment' && (
                            <div className="bg-[rgba(0,122,255,0.2)] px-2 py-1 rounded text-[10px] text-[#007AFF] font-semibold">
                              {mention.activity_type}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </LayoutHeader>
  );
};

export default SocialListeningApp;